class NotificationService {
  constructor() {
    this.notifications = this.loadNotifications();
    this.listeners = [];
  }

  // Load notifications from localStorage
  loadNotifications() {
    try {
      const saved = localStorage.getItem('crm_notifications');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading notifications:', error);
      return [];
    }
  }

  // Save notifications to localStorage
  saveNotifications() {
    try {
      localStorage.setItem('crm_notifications', JSON.stringify(this.notifications));
      this.notifyListeners();
    } catch (error) {
      console.error('Error saving notifications:', error);
    }
  }

  // Add listener for notification updates
  addListener(callback) {
    this.listeners.push(callback);
  }

  // Remove listener
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  // Notify all listeners
  notifyListeners() {
    this.listeners.forEach(callback => callback(this.notifications));
  }

  // Generate unique ID
  generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Create follow-up reminder notification
  createFollowUpReminder(lead, followUpDate, priority = 'medium') {
    const now = new Date();
    const followUp = new Date(followUpDate);
    const diffDays = Math.ceil((followUp - now) / (1000 * 60 * 60 * 24));
    
    let type = 'follow_up_due';
    let title = 'Follow-up Reminder';
    let message = '';

    if (diffDays < 0) {
      type = 'follow_up_overdue';
      title = 'Overdue Follow-up';
      message = `Follow-up for ${lead.name} is ${Math.abs(diffDays)} day(s) overdue`;
    } else if (diffDays === 0) {
      title = 'Follow-up Due Today';
      message = `Follow-up for ${lead.name} is due today`;
    } else if (diffDays === 1) {
      title = 'Follow-up Due Tomorrow';
      message = `Follow-up for ${lead.name} is due tomorrow`;
    } else {
      message = `Follow-up for ${lead.name} is due in ${diffDays} days`;
    }

    const notification = {
      id: this.generateId(),
      type,
      title,
      message,
      leadId: lead._id || lead.id,
      leadName: lead.name,
      leadPhone: lead.phone,
      leadEmail: lead.email,
      followUpDate: followUpDate,
      priority,
      read: false,
      createdAt: new Date().toISOString(),
      userId: lead.assignedTo || 'admin'
    };

    this.addNotification(notification);
    return notification;
  }

  // Create lead assignment notification
  createLeadAssignmentNotification(lead, assignedEmployee) {
    const notification = {
      id: this.generateId(),
      type: 'lead_assigned',
      title: 'New Lead Assigned',
      message: `Lead ${lead.name} has been assigned to you`,
      leadId: lead._id || lead.id,
      leadName: lead.name,
      leadPhone: lead.phone,
      leadEmail: lead.email,
      priority: 'medium',
      read: false,
      createdAt: new Date().toISOString(),
      userId: assignedEmployee.id || assignedEmployee._id
    };

    this.addNotification(notification);
    return notification;
  }

  // Create follow-up completion notification
  createFollowUpCompletedNotification(lead, followUpSummary) {
    const notification = {
      id: this.generateId(),
      type: 'follow_up_completed',
      title: 'Follow-up Completed',
      message: `Follow-up completed for ${lead.name}: ${followUpSummary}`,
      leadId: lead._id || lead.id,
      leadName: lead.name,
      leadPhone: lead.phone,
      priority: 'low',
      read: false,
      createdAt: new Date().toISOString(),
      userId: lead.assignedTo || 'admin'
    };

    this.addNotification(notification);
    return notification;
  }

  // Create general reminder notification
  createReminderNotification(title, message, userId, priority = 'medium') {
    const notification = {
      id: this.generateId(),
      type: 'reminder',
      title,
      message,
      priority,
      read: false,
      createdAt: new Date().toISOString(),
      userId
    };

    this.addNotification(notification);
    return notification;
  }

  // Add notification
  addNotification(notification) {
    this.notifications.unshift(notification);
    // Keep only last 100 notifications
    if (this.notifications.length > 100) {
      this.notifications = this.notifications.slice(0, 100);
    }
    this.saveNotifications();
  }

  // Get all notifications
  getAllNotifications() {
    return this.notifications;
  }

  // Get notifications for specific user
  getNotificationsForUser(userId) {
    return this.notifications.filter(n => n.userId === userId || n.userId === 'admin');
  }

  // Get unread notifications count
  getUnreadCount(userId = null) {
    const notifications = userId ? this.getNotificationsForUser(userId) : this.notifications;
    return notifications.filter(n => !n.read).length;
  }

  // Mark notification as read
  markAsRead(notificationId) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.saveNotifications();
    }
  }

  // Mark all notifications as read for user
  markAllAsRead(userId = null) {
    this.notifications.forEach(notification => {
      if (!userId || notification.userId === userId || notification.userId === 'admin') {
        notification.read = true;
      }
    });
    this.saveNotifications();
  }

  // Delete notification
  deleteNotification(notificationId) {
    this.notifications = this.notifications.filter(n => n.id !== notificationId);
    this.saveNotifications();
  }

  // Delete all notifications for a specific lead
  deleteNotificationsForLead(leadId) {
    this.notifications = this.notifications.filter(n => n.leadId !== leadId);
    this.saveNotifications();
  }

  // Clean up notifications for leads that no longer exist
  cleanupStaleNotifications(currentLeads) {
    const currentLeadIds = currentLeads.map(lead => lead._id || lead.id);
    const initialCount = this.notifications.length;
    
    // Keep only notifications that either don't have a leadId or have a leadId that exists in current leads
    this.notifications = this.notifications.filter(notification => {
      if (!notification.leadId) return true; // Keep general notifications
      return currentLeadIds.includes(notification.leadId);
    });
    
    const removedCount = initialCount - this.notifications.length;
    if (removedCount > 0) {
      console.log(`Cleaned up ${removedCount} stale notifications`);
      this.saveNotifications();
    }
    
    return removedCount;
  }

  // Check for overdue follow-ups and create notifications
  checkOverdueFollowUps(leads) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    leads.forEach(lead => {
      if (lead.followUps && lead.followUps.length > 0) {
        lead.followUps.forEach(followUp => {
          if (followUp.nextFollowUpDate && !followUp.completed) {
            const followUpDate = new Date(followUp.nextFollowUpDate);
            followUpDate.setHours(0, 0, 0, 0);

            // Check if already notified for this follow-up
            const existingNotification = this.notifications.find(n => 
              n.leadId === (lead._id || lead.id) && 
              n.followUpDate === followUp.nextFollowUpDate &&
              (n.type === 'follow_up_due' || n.type === 'follow_up_overdue')
            );

            if (!existingNotification) {
              if (followUpDate <= today) {
                this.createFollowUpReminder(lead, followUp.nextFollowUpDate, followUp.priority || 'medium');
              }
            }
          }
        });
      }
    });
  }

  // Check for upcoming follow-ups (1-3 days ahead)
  checkUpcomingFollowUps(leads) {
    const today = new Date();
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(today.getDate() + 3);

    leads.forEach(lead => {
      if (lead.followUps && lead.followUps.length > 0) {
        lead.followUps.forEach(followUp => {
          if (followUp.nextFollowUpDate && !followUp.completed) {
            const followUpDate = new Date(followUp.nextFollowUpDate);

            // Check if follow-up is in next 3 days
            if (followUpDate > today && followUpDate <= threeDaysFromNow) {
              // Check if already notified
              const existingNotification = this.notifications.find(n => 
                n.leadId === (lead._id || lead.id) && 
                n.followUpDate === followUp.nextFollowUpDate &&
                n.type === 'follow_up_due'
              );

              if (!existingNotification) {
                this.createFollowUpReminder(lead, followUp.nextFollowUpDate, followUp.priority || 'medium');
              }
            }
          }
        });
      }
    });
  }

  // Get today's follow-ups
  getTodaysFollowUps(leads, userId = null) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todaysFollowUps = [];

    leads.forEach(lead => {
      // Filter by user if specified
      if (userId && lead.assignedTo !== userId) return;

      if (lead.followUps && lead.followUps.length > 0) {
        lead.followUps.forEach(followUp => {
          if (followUp.nextFollowUpDate && !followUp.completed) {
            const followUpDate = new Date(followUp.nextFollowUpDate);
            followUpDate.setHours(0, 0, 0, 0);

            if (followUpDate.getTime() === today.getTime()) {
              todaysFollowUps.push({
                ...followUp,
                lead: lead,
                leadName: lead.name,
                leadPhone: lead.phone,
                leadEmail: lead.email
              });
            }
          }
        });
      }
    });

    return todaysFollowUps;
  }

  // Get overdue follow-ups
  getOverdueFollowUps(leads, userId = null) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const overdueFollowUps = [];

    leads.forEach(lead => {
      // Filter by user if specified
      if (userId && lead.assignedTo !== userId) return;

      if (lead.followUps && lead.followUps.length > 0) {
        lead.followUps.forEach(followUp => {
          if (followUp.nextFollowUpDate && !followUp.completed) {
            const followUpDate = new Date(followUp.nextFollowUpDate);
            followUpDate.setHours(0, 0, 0, 0);

            if (followUpDate < today) {
              const daysOverdue = Math.ceil((today - followUpDate) / (1000 * 60 * 60 * 24));
              overdueFollowUps.push({
                ...followUp,
                lead: lead,
                leadName: lead.name,
                leadPhone: lead.phone,
                leadEmail: lead.email,
                daysOverdue
              });
            }
          }
        });
      }
    });

    return overdueFollowUps.sort((a, b) => b.daysOverdue - a.daysOverdue);
  }

  // Clear all notifications
  clearAllNotifications() {
    this.notifications = [];
    this.saveNotifications();
  }

  // Force cleanup of all stale notifications immediately
  forceCleanupStaleNotifications() {
    // Remove all notifications that have leadId but no corresponding lead data
    const initialCount = this.notifications.length;
    this.notifications = this.notifications.filter(notification => {
      // Keep notifications without leadId (general notifications)
      return !notification.leadId;
    });
    
    const removedCount = initialCount - this.notifications.length;
    if (removedCount > 0) {
      console.log(`Force cleaned up ${removedCount} stale notifications`);
      this.saveNotifications();
    }
    
    return removedCount;
  }

  // Browser notification (if permission granted)
  showBrowserNotification(title, message, icon = null) {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body: message,
        icon: icon || '/favicon.ico',
        badge: '/favicon.ico'
      });
    }
  }

  // Request browser notification permission
  requestNotificationPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
      return Notification.requestPermission();
    }
    return Promise.resolve(Notification.permission);
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
